/**
 * @module off-canvas-panel
 */

import panelUpdater from '@remarkablenet/panel-updater';
import addDelegateEventListener from '../utility-functions/add-delegate-event-listener';
import { fetchText } from '../fetch-helper/fetch-helper';
import { activate, deactivate, visuallyDeactivate, toggle } from '../css-transition-helper/css-transition-helper';
import {
    getAbsoluteHeight,
    addClass,
    convertObjectValuesToArray,
    removeClass,
    checkForModuleToLoad,
    getModuleToLoad,
    bindModuleToLoad,
    appendScript,
    removeAppendedScript,
    getSrcToLoad
} from '../utility-functions/utility-functions';
import { dispatchEventWrapper } from '../event/event';
import { addToBasket, removeFromKit } from '../../pages/kitbuilder';

const PANEL_JS_LISTENER_CLASS_NAME = 'js-open-off-canvas-panel';
const PANEL_CLASS_NAME = 'off-canvas-panel';
const PANEL_BACKDROP_NAME = 'off-canvas-panel-backdrop';
const PANEL_MODIFIER_PREFIX = `${PANEL_CLASS_NAME}--`;
const PANEL_ACTIVE_STATE = 'is-active';
const PANEL_VISIBLE_STATE = 'is-visible';

const PanelPositions = Object.freeze({
    TOP: 'top',
    RIGHT: 'right',
    BOTTOM: 'bottom',
    LEFT: 'left'
});

const ocpOptions = Object.freeze({
    position: PanelPositions.RIGHT,
    //colour: 'white',
    autoHeight: true,
    extraClasses: '',
    hideCloseButton: false
});

const getCustomEvents = (el) => {
    let eventsArray = new Array();
    let events = el.dataset.customEvents;
    if (events) eventsArray['names'] = events.split(',');
    eventsArray['data'] = el.dataset.customEventData ? JSON.parse(el.dataset.customEventData) : null;
    return eventsArray;
};

const triggerCustomEvents = (events) => {
    if (events.names) {
        events.names.forEach((event) => {
            if (event !== undefined) {
                dispatchEventWrapper(document, event, events.data);
            }
        });
    }
};

export const addOffCanvasPanelListeners = (context = document) => {
    const panelLinks = context.querySelectorAll(`.${PANEL_JS_LISTENER_CLASS_NAME}`);

    panelLinks.forEach(async (panelLink) => {
        const link = panelLink as HTMLElement;
        if (link.dataset.ocpBound === 'true') return;
        link.dataset.ocpBound = 'true';

        panelLink.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            (panelLink as HTMLElement).blur();

            if (getAjaxUrl(panelLink)) {
                handleAjaxRequest(panelLink);
                document.querySelector<HTMLElement>('.js-close-quick-basket')?.click();
                let events = getCustomEvents((e.target as HTMLElement).parentElement);
                if (events) triggerCustomEvents(events);
                if (document.body.classList.contains('kitbuilder')) {
                    setTimeout(() => {
                        addToBasket();
                        removeFromKit(document.querySelector('.card--quick-basket'));
                    }, 2000);
                }
                return;
            }

            console.log('no url given');
            // Open ID Given
        });
    });
};

export const getAjaxUrl: any = (elm) => {
    if (elm) return elm.getAttribute('data-href') || elm.getAttribute('href');
};

export const addCloseButtonEventListerners = (panel, shouldDestroyOnClose = true) => {
    addDelegateEventListener(
        panel,
        'click',
        '.js-close-off-canvas-panel',
        (event) => {
            event.originalEvent.preventDefault();
            let events = getCustomEvents(event.delegateTarget.parentElement);
            if (events) triggerCustomEvents(events);
            handleCloseEvent(shouldDestroyOnClose);
        },
        false
    );
};

export const watchForPanelOpening = (panel) => panel.addEventListener('element.visible.activation', setPanelHeight);

export const setPanelHeight = (event) => {
    const panel = event.currentTarget;
    const child = panel.firstElementChild;
    const height = getAbsoluteHeight(child) + 40;
    panel.style.height = `${height}px`;
};

export const buildOptionsFromDataAttributes = (elm) => mapNodeMapIntoObjectOfOptions(elm);

export const mapNodeMapIntoObjectOfOptions = (nodeMap) => {
    let object = {};

    for (let i = 0; i < nodeMap.attributes.length; i++) {
        const attr = nodeMap.attributes[i];

        if (/^data-ocp/.test(attr.nodeName)) {
            let key = attr.nodeName.replace(/^data-ocp-/, '');
            let keyArray = key.split('-');
            let keyArrayFormatted = keyArray.map(uppercaseFirstCharacterInString);

            keyArrayFormatted[0] = keyArrayFormatted[0].toLowerCase();

            let finalKey = keyArrayFormatted.join('');
            let value = attr.nodeValue;

            object[finalKey] = value;
        }
    }

    return object;
};

export const visuallyDeactivateListOfPanels = (panels) => {
    panels.forEach((panels) => {
        visuallyDeactivate(panels);
    });
};

export const openOffCanvasPanel = (currentPanel, showBackdrop = false, shouldDestroyOnClose = false) => {
    const allActivePanels = selectAllActiveAndVisibleOffCanvasPanels();

    addCloseButtonEventListerners(currentPanel, shouldDestroyOnClose);

    if (!checkIfNodeListIsEmpty(allActivePanels)) {
        visuallyDeactivateListOfPanels(allActivePanels);
        activate(currentPanel);
        return;
    }

    if (showBackdrop) {
        createBackdrop().then((backdrop) => {
            activate(backdrop);
            backdrop.addEventListener('click', () => {
                handleCloseEvent(shouldDestroyOnClose);
            });
        });
    }

    activate(currentPanel);
};

export const handleCloseEvent = (shouldDestroyOnClose) => {
    destroyAllTemporaryVisuallyHiddenPanels();
    closeListOfOffCanvasPanels(selectAllVisibleOffCanvasPanels(), shouldDestroyOnClose);
};

export const destroyAllTemporaryVisuallyHiddenPanels = () => {
    const panels = selectAllTemporaryVisuallyHiddenOffCanvasPanels();
    if (!checkIfNodeListIsEmpty(panels)) {
        destroyListOfPanels(panels);
    }
};

export const closeListOfOffCanvasPanels = (panels, shouldDestroy = false) => {
    panels.forEach((panel) => {
        closeOffCanvasPanel(panel, shouldDestroy);
    });
};

export const closeOffCanvasPanel = (panel, shouldDestroy = false) => {
    const activeBackdrop = isABackdropActive();
    if (activeBackdrop) {
        deactivate(activeBackdrop).then(destroyBackdrop);
        destroyBackdrop();
    }

    deactivate(panel).then(() => {
        if (!shouldDestroy) return;
        removeAppendedScript();
        destroyPanel(panel);
    });
};

const _setPosition = (panelPositions, elm, position) => {
    const positionsArray = convertObjectValuesToArray(panelPositions);

    resetOffCanvasPanelPosition(elm, positionsArray);
    addClass(elm, [`${PANEL_MODIFIER_PREFIX}${position}`]);
};

export const createOffCanvasPanelInstance = (options = {}) => {
    return new Promise((resolve) => {
        const settings = { ...ocpOptions, ...options };

        const positionClass = `${PANEL_MODIFIER_PREFIX}${settings.position}`;
        const autoHeightClass = settings.autoHeight === true ? `${PANEL_MODIFIER_PREFIX}auto-height` : '';
        const extraClass = settings.extraClasses !== '' ? settings.extraClasses : '';
        const hideCloseButton = settings.hideCloseButton ? `${PANEL_MODIFIER_PREFIX}hide-close-btn` : '';

        const temporaryClass = `${PANEL_MODIFIER_PREFIX}temporary`;

        const panelHtml = document.createElement('div');
        const panelCloseBtn = document.createElement('div');

        panelHtml.setAttribute(
            'class',
            `${PANEL_CLASS_NAME} ${extraClass} ${positionClass} ${autoHeightClass} ${hideCloseButton} ${temporaryClass}`
        );
        panelCloseBtn.setAttribute('class', `${PANEL_CLASS_NAME}__close js-close-${PANEL_CLASS_NAME}`);
        if (settings['customEvents']) panelHtml.dataset.customEvents = settings['customEvents'];

        document.body.appendChild(panelHtml);

        resolve({
            panel: panelHtml,
            closeBtn: panelCloseBtn
        });
    }) as Promise<object>;
};

export const createBackdrop = () => {
    return new Promise((resolve) => {
        const backdropHtml = document.createElement('div');
        backdropHtml.setAttribute('class', PANEL_BACKDROP_NAME);

        document.body.appendChild(backdropHtml);
        resolve(backdropHtml);
    }) as Promise<HTMLDivElement>;
};

export const destroyListOfPanels = (panels) => {
    panels.forEach((panel) => {
        destroyPanel(panel);
    });
};

export const selectAllOffCanvasPanels = (context = document) => context.querySelectorAll(`.${PANEL_CLASS_NAME}`);
export const selectAllTemporaryOffCanvasPanels = (context = document) =>
    context.querySelectorAll(`.${PANEL_MODIFIER_PREFIX}temporary`);
export const selectAllTemporaryVisuallyHiddenOffCanvasPanels = (context = document) =>
    context.querySelectorAll(`.${PANEL_MODIFIER_PREFIX}temporary.${PANEL_VISIBLE_STATE}:not(.${PANEL_ACTIVE_STATE})`);
export const selectAllActiveOffCanvasPanels = (context = document) =>
    context.querySelectorAll(`.${PANEL_CLASS_NAME}.${PANEL_ACTIVE_STATE}`);
export const selectAllVisibleOffCanvasPanels = (context = document) =>
    context.querySelectorAll(`.${PANEL_CLASS_NAME}.${PANEL_VISIBLE_STATE}`);
export const selectAllActiveAndVisibleOffCanvasPanels = (context = document) =>
    context.querySelectorAll(`.${PANEL_CLASS_NAME}.${PANEL_ACTIVE_STATE}.${PANEL_VISIBLE_STATE}`);

export const visuallyDeactivatePanel = (panel) => panel.classList.remove(PANEL_ACTIVE_STATE);
export const visuallyActivatePanel = (panel) => panel.classList.add(PANEL_ACTIVE_STATE);

export const setOffCanvasPanelPosition = _setPosition.bind(this, PanelPositions);
export const resetOffCanvasPanelPosition = (elm, panelPositions) =>
    panelPositions.forEach((position) => removeClass(elm, position));

export const findClosestOffCanvasPanel = (elm) => elm.closest(`.${PANEL_CLASS_NAME}`);
export const findClosestOffCanvasPanelLink = (elm) =>
    elm.classList.contains(PANEL_JS_LISTENER_CLASS_NAME) ? elm : elm.closest(`.${PANEL_JS_LISTENER_CLASS_NAME}`);

export const destroyPanel = (panel) => panel.remove();
export const destroyBackdrop = () =>
    document.querySelectorAll(`.${PANEL_BACKDROP_NAME}`).forEach((item) => item.remove());

export const checkIfNodeListIsEmpty = (nodeListOfPanels) => nodeListOfPanels.length === 0;
export const isABackdropActive = () =>
    document.querySelector(`.${PANEL_BACKDROP_NAME}.${PANEL_ACTIVE_STATE}.${PANEL_VISIBLE_STATE}`) as HTMLElement;

const uppercaseFirstCharacterInString = (string) => string.charAt(0).toUpperCase() + string.slice(1);

export const handleAjaxRequest = (panelLink, callback: Function = null) => {
    if (panelLink) {
        let url = panelLink.getAttribute('data-href') || panelLink.getAttribute('href');
        url.indexOf('?') >= 0 ? (url += '&') : (url += '?');
        url += 'nolayout=true';

        const options = buildOptionsFromDataAttributes(panelLink);

        createOffCanvasPanelInstance(options).then(async (panelElements) => {
            const panel = panelElements['panel'];
            const html = await panelUpdater(panel, fetchText(url));

            // If the panel is for the log in slide out and user is logged in, go to account page
            if (panel.classList.contains('off-canvas-panel--account') && html.includes('About you')) {
                location.href = "/account";
                return;
            }

            panel.innerHTML = html;
            panel.appendChild(panelElements['closeBtn']);
            addOffCanvasPanelListeners(panel);

            if (checkForModuleToLoad(panel)) {
                const module = getModuleToLoad(panel);
                const moduleSrc = getSrcToLoad(checkForModuleToLoad(panel));

                appendScript(moduleSrc).then(() => {
                    bindModuleToLoad(module);
                });
            }

            watchForPanelOpening(panel);
            openOffCanvasPanel(panel, true, true);
            if (callback) callback();
        });
    }
};
